import { useState, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Pin, X, Edit2, Trash2 } from 'lucide-react';
import { AnnotationPostType, useAnnotationMutations, usePostAnnotations } from '@/hooks/use-annotations';
import { cn } from '@/lib/utils';

interface PostAnnotationsManagerWithPinsProps {
  postId: string;
  postType: AnnotationPostType;
  imageUrl?: string;
  thumbnailUrl?: string;
}

interface PinPosition {
  x: number; // percentage 0-100
  y: number; // percentage 0-100
}

export function PostAnnotationsManagerWithPins({ 
  postId, 
  postType,
  imageUrl,
  thumbnailUrl 
}: PostAnnotationsManagerWithPinsProps) {
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [placingPin, setPlacingPin] = useState(false);
  const [pendingPosition, setPendingPosition] = useState<PinPosition | null>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  const { annotations, isLoading } = usePostAnnotations(postId, postType, { includeSeen: true, enabled: true });
  const { create, update, remove } = useAnnotationMutations(postId, postType);

  const displayImage = imageUrl || thumbnailUrl;

  const handleImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!placingPin || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    setPendingPosition({ x, y });
    setPlacingPin(false);
  };

  const handleCreate = async () => {
    if (!content.trim()) return;
    try {
      await create.mutateAsync({ 
        content: content.trim(),
        metadata: pendingPosition ? { position: pendingPosition } : {}
      });
      setContent('');
      setPendingPosition(null);
    } catch (error) {
      console.error('[PostAnnotationsManagerWithPins] failed to create annotation', error);
    }
  };

  const handleToggleVisibility = async (annotationId: string, currentValue: boolean) => {
    try {
      await update.mutateAsync({ id: annotationId, isVisible: !currentValue });
    } catch (error) {
      console.error('[PostAnnotationsManagerWithPins] failed to toggle visibility', error);
    }
  };

  const handleStartEdit = (annotationId: string, existingContent: string) => {
    setEditingId(annotationId);
    setEditingContent(existingContent);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingContent.trim()) {
      return;
    }
    try {
      await update.mutateAsync({ id: editingId, content: editingContent.trim() });
      setEditingId(null);
      setEditingContent('');
    } catch (error) {
      console.error('[PostAnnotationsManagerWithPins] failed to update annotation', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const handleDelete = async (annotationId: string) => {
    try {
      await remove.mutateAsync(annotationId);
    } catch (error) {
      console.error('[PostAnnotationsManagerWithPins] failed to delete annotation', error);
    }
  };

  const handleCancelPlacement = () => {
    setPlacingPin(false);
    setPendingPosition(null);
  };

  return (
    <div className="space-y-6">
      {/* Image preview with pin placement */}
      {displayImage && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-[#D7DADC] font-medium">Pin placement</Label>
            {!placingPin && !pendingPosition && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPlacingPin(true)}
                className="border-[#4B5563] text-[#D7DADC]"
              >
                <Pin className="h-4 w-4 mr-2" />
                Place pin on image
              </Button>
            )}
            {placingPin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelPlacement}
                className="text-[#9CA3AF]"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>

          <div
            ref={imageRef}
            onClick={handleImageClick}
            className={cn(
              'relative rounded-lg overflow-hidden bg-[#0F0F10] border-2',
              placingPin 
                ? 'border-primary cursor-crosshair' 
                : 'border-[#343536]'
            )}
            style={{ aspectRatio: '16/9', maxHeight: '400px' }}
          >
            <img
              src={displayImage}
              alt="Post content"
              className="w-full h-full object-contain"
            />
            
            {/* Show pending pin position */}
            {pendingPosition && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: `${pendingPosition.x}%`,
                  top: `${pendingPosition.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <div className="relative">
                  <span className="absolute inline-flex h-8 w-8 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
                  </span>
                  <div className="relative inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-primary bg-primary/20 text-primary shadow-lg">
                    <Pin className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
            )}

            {/* Show existing pins */}
            {annotations.map((annotation) => {
              const metadata = annotation.metadata as { position?: { x?: number; y?: number } };
              const position = metadata?.position;
              if (!position?.x || !position?.y) return null;

              return (
                <div
                  key={annotation.id}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className={cn(
                    "inline-flex h-6 w-6 items-center justify-center rounded-full border border-primary/60 bg-primary/15 text-primary shadow-md",
                    annotation.has_seen ? 'opacity-60' : 'opacity-100'
                  )}>
                    <Pin className="h-3 w-3" />
                  </div>
                </div>
              );
            })}

            {placingPin && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                <div className="bg-[#1F1F23] px-4 py-2 rounded-lg border border-primary text-primary text-sm font-medium">
                  Click to place pin
                </div>
              </div>
            )}
          </div>

          {pendingPosition && (
            <p className="text-xs text-emerald-400">
              ✓ Pin placed at {pendingPosition.x.toFixed(1)}%, {pendingPosition.y.toFixed(1)}% - add your note below
            </p>
          )}
        </div>
      )}

      {/* Annotation content */}
      <div className="space-y-2">
        <Label htmlFor="annotation-content" className="text-[#D7DADC] font-medium">
          {pendingPosition ? 'Add note for this pin' : 'Add a new annotation'}
        </Label>
        <Textarea
          id="annotation-content"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder="Share an artist note or behind-the-scenes detail..."
          className="min-h-[120px] resize-none bg-[#0F0F10] border-[#343536] text-[#D7DADC] placeholder:text-gray-400"
          maxLength={1000}
        />
        <div className="flex items-center justify-between text-xs text-[#9CA3AF]">
          <span>{content.length} / 1000</span>
          <div className="flex gap-2">
            {pendingPosition && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelPlacement}
              >
                Clear pin
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!content.trim() || create.isPending}
            >
              {create.isPending ? 'Adding...' : 'Add annotation'}
            </Button>
          </div>
        </div>
      </div>

      {/* Existing annotations */}
      <div className="space-y-3">
        <div className="text-sm text-[#9CA3AF] font-medium uppercase tracking-wide">
          Existing annotations
        </div>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full bg-[#0F0F10]" />
            <Skeleton className="h-20 w-full bg-[#0F0F10]" />
          </div>
        ) : annotations.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">
            {displayImage 
              ? 'No annotations added yet. Click "Place pin on image" to get started.' 
              : 'No annotations added yet. Create your first note above.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {annotations.map((annotation) => {
              const isEditing = editingId === annotation.id;
              const relativeCreated = formatDistanceToNow(new Date(annotation.created_at), { addSuffix: true });
              const metadata = annotation.metadata as { position?: { x?: number; y?: number } };
              const hasPosition = !!(metadata?.position?.x && metadata?.position?.y);

              return (
                <li
                  key={annotation.id}
                  className="rounded-lg border border-[#343536] bg-[#1F1F23] p-4 space-y-3"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#9CA3AF]">
                    {hasPosition && (
                      <span className="rounded bg-primary/20 px-2 py-0.5 text-primary flex items-center gap-1">
                        <Pin className="h-3 w-3" />
                        Pinned
                      </span>
                    )}
                    <span>Created {relativeCreated}</span>
                    {annotation.updated_at !== annotation.created_at && (
                      <span className="text-[#71717A]">• Updated {formatDistanceToNow(new Date(annotation.updated_at), { addSuffix: true })}</span>
                    )}
                    {annotation.has_seen && <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-300">Viewers have seen</span>}
                    {!annotation.is_visible && <span className="rounded bg-amber-500/20 px-2 py-0.5 text-amber-300">Hidden</span>}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingContent}
                        onChange={(event) => setEditingContent(event.target.value)}
                        maxLength={1000}
                        className="min-h-[100px] resize-none bg-[#0F0F10] border-[#343536] text-[#D7DADC] placeholder:text-gray-400"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editingContent.trim() || update.isPending}
                        >
                          {update.isPending ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#E5E7EB] whitespace-pre-line">{annotation.content}</p>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2A2D] pt-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`annotation-visible-${annotation.id}`}
                        checked={annotation.is_visible}
                        onCheckedChange={() => handleToggleVisibility(annotation.id, annotation.is_visible)}
                        disabled={update.isPending}
                      />
                      <Label
                        htmlFor={`annotation-visible-${annotation.id}`}
                        className={cn('text-sm', annotation.is_visible ? 'text-[#D7DADC]' : 'text-[#9CA3AF]')}
                      >
                        {annotation.is_visible ? 'Visible to viewers' : 'Hidden from viewers'}
                      </Label>
                    </div>

                    {!isEditing && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartEdit(annotation.id, annotation.content)}
                          disabled={update.isPending}
                          className="border-[#4B5563] text-[#D7DADC]"
                        >
                          <Edit2 className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(annotation.id)}
                          disabled={remove.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {remove.isPending ? 'Removing...' : 'Delete'}
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PostAnnotationsManagerWithPins;
